import { createContext, useEffect, useState } from 'react';
import { Caby, CabysResponse, Group, Menu, MenuResponse, Merchant, MerchantsAndGroupsResponse, MerchantTypeResponse, Product, ProductResponse, ProductsResponse, TaxCodeResponse, Type, Units, UnitsResponse } from '../../models/interfaces';
import topApi from '../../components/api/ToPApi';




type ProductsContextProps = {
    products: Menu[];
    groups: Group[];
    merchants: Merchant[];
    suggestions: string[];
    totalPage: number;
    isLoading: boolean;
    selectedCabys: Caby;
    loadProducts: () => Promise<void>;
    getMenus: () => Promise<Menu[]>;
    addMenu: (menu: Menu) => Promise<string>;
    updateMenu: (menu: Menu) => Promise<string>;
    deleteMenu: (id: number) => Promise<string>;
    addProduct: (product: Product, menuId: number) => Promise<string>;
    updateProduct: (product: Product) => Promise<string>;
    deleteProduct: (id: number) => Promise<string>;
    getMenusAndSubmenus: () => Promise<Menu[]>;
    getCabysByCodeOrName: (searchTerm: string, pageActual: number) => Promise<Caby[]>;
    getPrincipalCabys: () => Promise<Caby[]>;
    getCabysByCategory: (nivel: number, parentCategory: number) => Promise<Caby[]>;
    getCabysByCategory8: (searchTerm: string) => Promise<Caby[]>;
    setCabys: (caby: Caby) => Promise<void>;

    loadTaxCode: () => Promise<Type[]>;
    loadUnits: () => Promise<Units[]>;
    // updateProducts: ( Products: Products ) => Promise<string>;
    // deleteProducts: ( id: number ) => Promise<string>;
    // loadMerchantsAndGroups: () => Promise<void>;
    // clearSuggestions: () => void;

}

export const ProductsContext = createContext({} as ProductsContextProps);
export const TransactionsContext = createContext({} as ProductsContextProps);

export const ProductsProvider = ({ children }: any) => {

    const [products, setProducts] = useState<Menu[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [merchants, setMerchants] = useState<Merchant[]>([]);
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [totalPage, setTotalPage] = useState<number>(0);
    const pageSize = 15;
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCabys, setSelectedCabys] = useState<Caby>(null);


    const loadTaxCode = async () => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<TaxCodeResponse>('/product/getTaxCode');
            if (resp.status == 200) {
                return resp.data.codes;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }

    const loadUnits = async () => {

        const resp = await topApi.get<UnitsResponse>('/stock/getUnits');
        if (resp.status == 200) {
            return resp.data.units;
        } else {
            return [];
        }
    }


    const loadProducts = async () => {
        try {
            setIsLoading(true);
            const resp = await topApi.get<ProductsResponse>('/menu/getMenu');
            setProducts(resp.data.menu);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
        }

    }

    const getMenus = async () => {
        return products.filter((menu) => menu.parentId == null);
    }

    const getMenusAndSubmenus = async () => {
        return flattenMenus(products);
    }

    const flattenMenus = (menus: Menu[]): Menu[] => {
        let result: Menu[] = [];

        menus.forEach((menu) => {
            // Agregar el menú actual
            result.push(menu);

            // Si tiene submenús, llamar recursivamente y agregar los resultados
            if (menu.submenu && menu.submenu.length > 0) {
                result = result.concat(flattenMenus(menu.submenu));
            }
        });

        return result;
    };

    const addMenu = async (menu: Menu) => {
        try {
            const resp = await topApi.post<MenuResponse>('/menu/', { menu });
            if (resp.data.error) {
                return resp.data.error
            } else {
                setProducts([...products, resp.data.menu]);
                return 'success'
            }


        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const updateMenu = async (menu: Menu) => {
        try {
            const resp = await topApi.put<Menu>(`/menu/${menu.id}`, { menu });

            loadProducts();

            return 'success'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const deleteMenu = async (id: number) => {
        try {
            await topApi.delete<Menu>(`/menu/${id}`);
            loadProducts();
            return 'success'
        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }



    const addProduct = async (product: Product) => {
        try {

            const resp = await topApi.post<ProductResponse>('/product/', { product });
            if (resp.data.error) {
                return resp.data.error
            } else {
                loadProducts();
                return 'success'
            }


        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const updateProduct = async (product: Product) => {
        try {
            const resp = await topApi.put<Product>(`/product/${product.id}`, { product });

            loadProducts();

            return 'success'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const deleteProduct = async (id: number) => {
        try {
            await topApi.delete<Product>(`/product/${id}`);
            loadProducts();
            return 'success'
        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }



    const getCabysByCodeOrName = async (searchTerm: string, pageActual: number) => {
        try {
            setIsLoading(true);
            const resp = await topApi.get<CabysResponse>('/product/getCabysByCodeOrName/' + encodeURIComponent(searchTerm) + '/' + pageActual + '/' + pageSize);
            if (resp.status == 200) {
                setTotalPage(resp.data.totalPages);
                setIsLoading(false);
                return resp.data.cabys
            }
            setIsLoading(false);
            return [];
        } catch (error) {
            setIsLoading(false);
            return [];
        }
    }



    const getPrincipalCabys = async () => {
        try {
            setIsLoading(true);
            const resp = await topApi.get<CabysResponse>('/product/getPrincipalCabys/');
            if (resp.status == 200) {
                setTotalPage(resp.data.totalPages);
                setIsLoading(false);
                return resp.data.cabys
            }
            setIsLoading(false);
            return [];
        } catch (error) {
            setIsLoading(false);
            return [];
        }
    }



    const getCabysByCategory = async (nivel: number, parentCategory: number) => {
        try {
            setIsLoading(true);
            const resp = await topApi.get<CabysResponse>('/product/getCabysByCategory/' + nivel + '/' + parentCategory);
            if (resp.status == 200) {
                setTotalPage(resp.data.totalPages);
                setIsLoading(false);
                return resp.data.cabys
            }
            setIsLoading(false);
            return [];
        } catch (error) {
            setIsLoading(false);
            return [];
        }
    }

    const getCabysByCategory8 = async (searchTerm: string) => {
        try {
            setIsLoading(true);
            const resp = await topApi.get<CabysResponse>('/product/getCabysByCategory8/' + searchTerm);
            if (resp.status == 200) {
                setIsLoading(false);
                return resp.data.cabys
            }
            setIsLoading(false);
            return [];
        } catch (error) {
            setIsLoading(false);
            return [];
        }
    }

    const setCabys = async (caby: Caby) => {
        try {
            setSelectedCabys(caby);
        } catch (error) {

        }
    }


    return (
        <ProductsContext.Provider value={{
            products,
            groups,
            merchants,
            suggestions,
            totalPage,
            isLoading,
            loadProducts,
            getMenus,
            addMenu,
            updateMenu,
            deleteMenu,
            addProduct,
            updateProduct,
            deleteProduct,
            getMenusAndSubmenus,
            getCabysByCodeOrName,
            getPrincipalCabys,
            getCabysByCategory,
            selectedCabys,
            setCabys,
            getCabysByCategory8,
            loadTaxCode,
            loadUnits
        }}>
            {children}
        </ProductsContext.Provider>
    );
}